import {Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {App} from '../../../../../../../shared/models/app';
import {AppTemplateOccupantTenant} from '../../../../../../../shared/models/app-template/tenant-portal/occupant';
import {ModuleOccupantHvacZone} from '../../../../../../../shared/models/app-template/tenant-portal/modules';
import {ShadeToolsService} from '../../../../../../../core/services/shade-tools.service';
import {TilesService} from '../../../../../../../core/services/tiles.service';
import {
  TileFormat,
  TileId, TileType,
} from '../../../../../../../shared/models/tile';
import {makeTileFromBacnetPoint} from '../../../../../../../shared/models/bacnet';
import {DeviceType} from '../../../../../../../shared/models/device';
import {AppsService} from '../../../../../../../core/services/apps.service';
import {
  TileSelectorButtonCallback,
  TileSelectorButtonComponent,
} from '../../../../../../components/tile-selector-button/tile-selector-button.component';
import {UrlService} from '../../../../../../../core/services/url.service';
import {
  BacnetPointSelectionCallback,
  ModbusPointSelectionCallback,
  tileFormatToTileTypeMap,
} from '../../../../../../../shared/models/point';
import {ModuleItem} from '../../../../../../../shared/models/app-template/module';
import {DevicesService} from '../../../../../../../core/services/devices.service';
import {Protocol} from '../../../../../../../shared/models/protocol';
import {makeTileFromModbusPoint, ModbusPoint} from '../../../../../../../shared/models/modbus';
import {DeviceId} from '../../../../../../../shared/models/device';
import {DialogBoxOption, DialogBoxButtonColor} from '../../../../../../../shared/models/dialog-box';
import {NgClass} from '@angular/common';
import {SpinnerComponent} from '../../../../../../components/spinner/spinner.component';
import {RouterLink} from '@angular/router';
import {BacnetPointDiscoveryComponent} from '../../../../../../components/bacnet-point-discovery/bacnet-point-discovery.component';
import {ModbusPointEditorComponent} from '../../../../../../components/modbus-point-editor/modbus-point-editor.component';
import { EMPTY_ID } from 'src/shared/constants/empty-id';

@Component({
  selector: 'motn-configure-occupant-tenant',
  templateUrl: './configure-occupant-tenant.component.html',
  styleUrls: ['./configure-occupant-tenant.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TileSelectorButtonComponent,
    NgClass,
    SpinnerComponent,
    RouterLink,
    BacnetPointDiscoveryComponent,
    ModbusPointEditorComponent,
  ],
})
export class ConfigureOccupantTenantComponent implements OnInit, OnDestroy {
  @Input() app: App;
  template: AppTemplateOccupantTenant;


  submittingHvac: boolean = false;

  /**
   * When set to non-empty value, BACnet discovery will appear
   */
  bacnetDiscoveryUseCase: string = '';
  bacnetDiscoveryCallback: BacnetPointSelectionCallback;
  bacnetFormatRestriction: TileFormat;

  /**
   * When set to non-empty value, Modbus point editor will appear
   */
  modbusConfigurationUseCase: string = '';
  modbusConfigurationCallback: ModbusPointSelectionCallback;
  modbusFormatRestriction: TileFormat;

  hvacZones: ModuleOccupantHvacZone[];

  useCaseSetPointString: string = 'Set Point';
  useCaseSetPointCoolString: string = 'Set Point (Cool)';
  useCaseSetPointHeatString: string = 'Set Point (Heat)';


  constructor(
    private appsService: AppsService,
    private devicesService: DevicesService,
    private tilesService: TilesService,
    private shadeTools: ShadeToolsService,
  ) {
  }


  ngOnInit(): void {
    this.template = this.app.Config.Template;

    this.initialSetup();

    this.hvacZones = this.template?.Modules?.HVAC?.Zones;
    if (!this.hvacZones) {

    }
  }


  ngOnDestroy() {
    this.shadeTools.closeAll();
  }


  saveHvac(): void {
    this.submittingHvac = true;

    this.appsService.patchAppTemplate$(this.app.ID, this.template).subscribe(
      () => {
        this.shadeTools.simpleOkDialog(
          `Success`,
          `The changes were saved successfully and will take effect immediately.`,
        );
        this.submittingHvac = false;
      },
      () => {
        this.shadeTools.errorDialog();
        this.submittingHvac = false;
      },
    );
  }


  initialSetup(): void {
    if (!this.template.Modules) {
      this.template.Modules = {};
    }

    if (typeof this.template.Modules.HVAC === 'undefined') {
      this.template.Modules.HVAC = {
        Zones: [
          {
            Name: 'Main',
            SetPoint: {
              TileID: EMPTY_ID,
              Disabled: false,
            },
            SetPointCool: {
              TileID: EMPTY_ID,
              Disabled: false,
            },
            SetPointHeat: {
              TileID: EMPTY_ID,
              Disabled: false,
            },
            Temperature: {
              TileID: EMPTY_ID,
              Disabled: false,
            },
            Humidity: {
              TileID: EMPTY_ID,
              Disabled: false,
            },
            SystemMode: {
              TileID: EMPTY_ID,
              Disabled: false,
            },
            FanAlwaysOn: {
              TileID: EMPTY_ID,
              Disabled: false,
            },
            OnOff: {
              TileID: EMPTY_ID,
              Disabled: false,
            },
          },
        ],
      };
    }

    if (typeof this.template.Modules.Access === 'undefined') {
      this.template.Modules.Access = {
        MainDoor: {},
      };
    }

    if (typeof this.template.Modules.PowerMeter === 'undefined') {
      this.template.Modules.PowerMeter = {};
    }

    if (typeof this.template.Modules.Announcements === 'undefined') {
      this.template.Modules.Announcements = {};
    }
  }


  getTenantPortalHref(): string {
    return UrlService.makeTenantPortalDashboardUrl(this.app.ID);
  }


  /**
   * Preps the menu options related to brand new tile creation for when user
   * clicks any zone's set point tile button
   *
   * @param zoneIndex
   */
  getHvacSetPointMenuCreateCallback(zoneIndex: number): TileSelectorButtonCallback {
    return () => {
      this.startNewTileCreator(this.hvacZones[zoneIndex].SetPoint, this.useCaseSetPointString, TileFormat.Numeric);
    };
  }


  /**
   * Preps the menu options related to brand new tile creation for when user
   * clicks any zone's set point tile button
   *
   * @param zoneIndex
   */
  getHvacSetPointCoolMenuCreateCallback(zoneIndex: number): TileSelectorButtonCallback {
    return () => {
      this.startNewTileCreator(this.hvacZones[zoneIndex].SetPointCool, this.useCaseSetPointCoolString, TileFormat.Numeric);
    };
  }


  /**
   * Preps the menu options related to brand new tile creation for when user
   * clicks any zone's set point tile button
   *
   * @param zoneIndex
   */
  getHvacSetPointHeatMenuCreateCallback(zoneIndex: number): TileSelectorButtonCallback {
    return () => {
      this.startNewTileCreator(this.hvacZones[zoneIndex].SetPointHeat, this.useCaseSetPointHeatString, TileFormat.Numeric);
    };
  }


  /**
   * Preps the menu options related to brand new tile creation for when user
   * clicks any zone's temperature tile button
   *
   * @param zoneIndex
   */
  getHvacTemperatureMenuCreateCallback(zoneIndex: number): TileSelectorButtonCallback {
    return () => {
      this.startNewTileCreator(this.hvacZones[zoneIndex].Temperature, 'Temperature', TileFormat.Numeric);
    };
  }


  /**
   * Preps the menu options related to brand new tile creation for when user
   * clicks any zone's humidity tile button
   *
   * @param zoneIndex
   */
  getHvacHumidityMenuCreateCallback(zoneIndex: number): TileSelectorButtonCallback {
    return () => {
      this.startNewTileCreator(this.hvacZones[zoneIndex].Humidity, 'Humidity', TileFormat.Numeric);
    };
  }


  /**
   * Preps the menu options related to brand new tile creation for when user
   * clicks any zone's humidity tile button
   *
   * @param zoneIndex
   */
  getHvacSystemModeMenuCreateCallback(zoneIndex: number): TileSelectorButtonCallback {
    return () => {
      this.startNewTileCreator(this.hvacZones[zoneIndex].SystemMode, 'System Mode', TileFormat.Multichoice);
    };
  }


  /**
   * Preps the menu options related to brand new tile creation for when user
   * clicks any zone's humidity tile button
   *
   * @param zoneIndex
   */
  getHvacFanAlwaysOnMenuCreateCallback(zoneIndex: number): TileSelectorButtonCallback {
    return () => {
      this.startNewTileCreator(this.hvacZones[zoneIndex].FanAlwaysOn, 'Fan Always On', TileFormat.Toggle);
    };
  }


  /**
   * Preps the menu options related to brand new tile creation for when user
   * clicks any zone's on/off tile button
   *
   * @param zoneIndex
   */
  getHvacOnOffMenuCreateCallback(zoneIndex: number): TileSelectorButtonCallback {
    return () => {
      this.startNewTileCreator(this.hvacZones[zoneIndex].OnOff, 'System On/Off', TileFormat.Toggle);
    };
  }


  /**
   * Determines what types of devices (i.e. protocols) the user has. If more than one,
   * will present options for each protocol to enter point discovery/configuration.
   * Otherwise just opens point discovery/configuration for the one type of device the
   * user has. If user has no devices, presents an error.
   *
   * @param moduleItem
   * @param useCase
   * @param tileFormat
   * @private
   */
  private startNewTileCreator(moduleItem: ModuleItem, useCase: string, tileFormat: TileFormat) {
    this.devicesService.fetchUserDevices$().subscribe(
      (devices) => {
        const protocols: Protocol[] = [];

        for (let device of devices) {
          if (!protocols.includes(device.Type)) {
            protocols.push(device.Type);
          }
        }

        switch (protocols.length) {
          case 0:
            this.shadeTools.errorDialog('You do not have any devices, so you cannot create new tiles.');
            break;
          case 1:
            switch (protocols[0]) {
              case 'bacnet':
                this.startBacnetDiscovery(moduleItem, useCase, tileFormat);
                break;
              case 'modbus':
                this.startModbusDiscovery(moduleItem, useCase, tileFormat);
                break;
              default:
                this.shadeTools.errorDialog('You do not have any devices that support tile creation in this app.');
                break;
            }

            break;
          default:
            const options: DialogBoxOption[] = [];

            if (protocols.includes(DeviceType.BACnet)) {
              options.push({
                text: 'BACnet/IP',
                callback: () => {
                  this.startBacnetDiscovery(moduleItem, useCase, tileFormat);
                },
              });
            }

            if (protocols.includes(DeviceType.Modbus)) {
              options.push({
                text: 'Modbus TCP',
                callback: () => {
                  this.startModbusDiscovery(moduleItem, useCase, tileFormat);
                },
              });
            }

            this.shadeTools.startDialog(
              'Protocol',
              'Choose the protocol of the device that contains the point for which you want to create a new tile.',
              [
                ...options,
                {
                  text: 'Cancel',
                  callback: () => {
                  },
                  color: DialogBoxButtonColor.Alt,
                },
              ],
            );
        }
      },
      () => {
        this.shadeTools.errorDialog('Could not fetch devices. Please try again.');
      },
    );
  }


  private startBacnetDiscovery(moduleItem: ModuleItem, useCase: string, tileFormat: TileFormat) {
    const tileType: TileType = tileFormatToTileTypeMap[tileFormat];

    this.bacnetFormatRestriction = tileFormat;
    this.bacnetDiscoveryUseCase = useCase;
    this.bacnetDiscoveryCallback = (p, d) => {
      const tile = makeTileFromBacnetPoint(p, tileType, true, d.ID);

      this.tilesService.createTile$(tile).subscribe(
        (tileId) => {
          moduleItem.TileID = tileId;
          this.stopBacnetDiscovery();
        },
      );
    };
  }


  private startModbusDiscovery(moduleItem: ModuleItem, useCase: string, tileFormat: TileFormat) {
    const tileType: TileType = tileFormatToTileTypeMap[tileFormat];

    this.modbusFormatRestriction = tileFormat;
    this.modbusConfigurationUseCase = useCase;
    this.modbusConfigurationCallback = (p: ModbusPoint, tt: TileType, d: DeviceId) => {
      const tile = makeTileFromModbusPoint(p, tt, d);

      this.tilesService.createTile$(tile).subscribe(
        (tileId) => {
          moduleItem.TileID = tileId;
          this.stopModbusConfiguration();
        },
      );
    };
  }


  stopBacnetDiscovery(): void {
    this.bacnetDiscoveryUseCase = '';
    this.bacnetDiscoveryCallback = null;
  }


  stopModbusConfiguration(): void {
    this.modbusConfigurationUseCase = '';
    this.modbusConfigurationCallback = null;
  }


  /**
   *
   * @param zoneIndex
   * @param tileId
   */
  setHvacSetPointExistingTile(zoneIndex: number, tileId: TileId) {
    this.hvacZones[zoneIndex].SetPoint.TileID = tileId;
  }


  /**
   *
   * @param zoneIndex
   * @param tileId
   */
  setHvacSetPointCoolExistingTile(zoneIndex: number, tileId: TileId) {
    this.hvacZones[zoneIndex].SetPointCool.TileID = tileId;
  }


  /**
   *
   * @param zoneIndex
   * @param tileId
   */
  setHvacSetPointHeatExistingTile(zoneIndex: number, tileId: TileId) {
    this.hvacZones[zoneIndex].SetPointHeat.TileID = tileId;
  }


  /**
   *
   * @param zoneIndex
   * @param tileId
   */
  setHvacTemperatureExistingTile(zoneIndex: number, tileId: TileId) {
    this.hvacZones[zoneIndex].Temperature.TileID = tileId;
  }


  /**
   *
   * @param zoneIndex
   * @param tileId
   */
  setHvacHumidityExistingTile(zoneIndex: number, tileId: TileId) {
    this.hvacZones[zoneIndex].Humidity.TileID = tileId;
  }


  /**
   *
   * @param zoneIndex
   * @param tileId
   */
  setHvacSystemModeExistingTile(zoneIndex: number, tileId: TileId) {
    this.hvacZones[zoneIndex].SystemMode.TileID = tileId;
  }


  /**
   *
   * @param zoneIndex
   * @param tileId
   */
  setHvacFanAlwaysOnExistingTile(zoneIndex: number, tileId: TileId) {
    this.hvacZones[zoneIndex].FanAlwaysOn.TileID = tileId;
  }


  /**
   *
   * @param zoneIndex
   * @param tileId
   */
  setHvacOnOffMenuExistingTile(zoneIndex: number, tileId: TileId) {
    this.hvacZones[zoneIndex].OnOff.TileID = tileId;
  }
}
